import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

const CONTRACTOR_TYPES = [
  'general',
  'subcontractor',
  'owner',
  'architect_engineer',
  'consultant',
  'supplier',
  'other',
];

const VALID_TRADES = [
  'concrete', 'carpentry', 'electrical', 'plumbing', 'hvac',
  'roofing', 'masonry', 'steel', 'drywall', 'painting',
  'flooring', 'landscaping', 'excavation', 'demolition',
  'glazing', 'insulation', 'other',
];

const COMPANY_SIZES = [
  '', '1-10', '11-50', '51-200', '201-500', '500+',
];

const COUNTRIES = [
  'CA', 'US', 'MX', 'GB', 'FR', 'DE', 'ES', 'IT', 'NL', 'BE',
  'CH', 'PT', 'AU', 'IE', 'AT', 'SE', 'NO', 'DK', 'FI', 'PL',
  'AE', 'SA', 'QA', 'IN', 'CN', 'JP', 'BR', 'AR', 'CL', 'CO',
  'NZ', 'SG', 'KR', 'ZA',
];

const CreateOrgModal = React.memo(({ isOpen, onClose, firebase, editMode = false, initialOrgName = '' }) => {
  const { t, i18n } = useTranslation();

  const [orgName, setOrgName] = useState('');
  const [contractorType, setContractorType] = useState('general');
  const [trade, setTrade] = useState([]);
  const [companySize, setCompanySize] = useState('');
  const [country, setCountry] = useState('');
  const [website, setWebsite] = useState('');
  const [createMessage, setCreateMessage] = useState({ type: '', text: '' });
  const [createLoading, setCreateLoading] = useState(false);
  const [loadingOrg, setLoadingOrg] = useState(false);

  const countryNames = useMemo(() => {
    try {
      return new Intl.DisplayNames([i18n.language || 'en'], { type: 'region' });
    } catch {
      return null;
    }
  }, [i18n.language]);

  const getCountryName = useCallback((code) => {
    if (countryNames) {
      try {
        const name = countryNames.of(code);
        if (name && name !== code) return name;
      } catch {}
    }
    return code;
  }, [countryNames]);

  // Body scroll lock while open
  useEffect(() => {
    if (!isOpen) return;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !editMode || !initialOrgName) return;
    
    let cancelled = false;
    setLoadingOrg(true);
    setCreateMessage({ type: '', text: '' });
    
    firebase.doGetOrgInfo(initialOrgName)
      .then((orgInfo) => {
        if (cancelled) return;
        if (orgInfo) {
          setOrgName(orgInfo.name || initialOrgName);
          setContractorType(orgInfo.contractorType || 'general');
          setTrade(Array.isArray(orgInfo.trade) ? orgInfo.trade : []);
          setCompanySize(orgInfo.companySize || '');
          setCountry(orgInfo.country || '');
          setWebsite(orgInfo.website || '');
        } else {
          setOrgName(initialOrgName);
        }
      })
      .catch((error) => {
        if (cancelled) return;
        setCreateMessage({ type: 'error', text: error.message });
        setOrgName(initialOrgName);
      })
      .finally(() => {
        if (!cancelled) setLoadingOrg(false);
      });
    
    return () => { cancelled = true; };
  }, [isOpen, editMode, initialOrgName, firebase]);

  // Reset form when modal closes
  useEffect(() => {
    if (isOpen) return;
    setOrgName('');
    setContractorType('general');
    setTrade([]);
    setCompanySize('');
    setCountry('');
    setWebsite('');
    setCreateMessage({ type: '', text: '' });
    setLoadingOrg(false);
  }, [isOpen]);

  const handleClose = useCallback(() => {
    if (createLoading) return;
    onClose();
  }, [onClose, createLoading]);

  const handleSubmit = useCallback(async (e) => {
    if (e) e.preventDefault();
    
    if (editMode) {
      if (!initialOrgName) return;
      setCreateLoading(true);
      try {
        await firebase.doUpdateOrganization(initialOrgName, {
          contractorType,
          trade,
          companySize,
          country,
          website,
        });
        setCreateMessage({ type: 'success', text: t('account.org_updated_success') });
        setTimeout(() => {
          setCreateMessage({ type: '', text: '' });
          onClose();
        }, 1200);
      } catch (error) {
        setCreateMessage({ type: 'error', text: error.message });
      }
      setCreateLoading(false);
    } else {
      if (!orgName.trim()) {
        setCreateMessage({ type: 'error', text: t('account.org_name_required') });
        return;
      }
      setCreateLoading(true);
      try {
        await firebase.doCreateOrganization(orgName.trim(), {
          contractorType,
          trade,
          companySize,
          country,
          website,
        });
        setCreateMessage({ type: 'success', text: t('account.org_created_success') });
        setTimeout(() => {
          setCreateMessage({ type: '', text: '' });
          onClose();
        }, 1200);
      } catch (error) {
        setCreateMessage({ type: 'error', text: error.message });
      }
      setCreateLoading(false);
    }
  }, [editMode, initialOrgName, orgName, contractorType, trade, companySize, country, website, firebase, t, onClose]);

  if (!isOpen) return null;

  return createPortal(
    <div className="create-org-backdrop" onClick={handleClose}>
      <div
        className="create-org-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="create-org-modal-title"
      >
        {/* Header */}
        <div className="co-modal-header">
          <h2 id="create-org-modal-title">
            {editMode ? t('account.edit_org_modal_title') : t('account.create_org_modal_title')}
          </h2>
          <button
            className="co-close-button"
            onClick={handleClose}
            title={t('cancel')}
            disabled={createLoading || loadingOrg}
            type="button"
          >
            <span aria-hidden="true">&times;</span>
          </button>
        </div>

        {/* Body */}
        <div className="co-modal-body">
          {loadingOrg ? (
            <div className="co-loading-state">
              <div className="co-loading-spinner" />
              <span>{t('loading')}</span>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="org-form" noValidate>
              <div className="co-form-group">
                <label>{t('account.org_name_label')}</label>
                {editMode ? (
                  <input
                    type="text"
                    value={orgName}
                    disabled
                    readOnly
                  />
                ) : (
                  <input
                    type="text"
                    value={orgName}
                    onChange={(e) => setOrgName(e.target.value)}
                    placeholder={t('account.org_name_placeholder')}
                    disabled={createLoading}
                    autoFocus
                  />
                )}
              </div>

            <div className="co-form-group">
              <label>{t('account.contractor_type')}</label>
              <select
                value={contractorType}
                onChange={(e) => setContractorType(e.target.value)}
                disabled={createLoading || loadingOrg}
              >
                {CONTRACTOR_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {t(`account.contractor_type_${type}`)}
                  </option>
                ))}
              </select>
            </div>

            <div className="co-form-group trade-group">
              <label>{t('account.primary_trades')}</label>
              <div className="trade-chip-group" role="group" aria-label={t('account.primary_trades')}>
                {VALID_TRADES.map((tr) => {
                  const selected = trade.includes(tr);
                  return (
                    <button
                      key={tr}
                      type="button"
                      className={`trade-chip ${selected ? 'selected' : ''}`}
                      aria-pressed={selected}
                      disabled={createLoading || loadingOrg}
                      onClick={() => {
                        setTrade((prev) =>
                          selected ? prev.filter((x) => x !== tr) : [...prev, tr]
                        );
                      }}
                    >
                      {t(`account.trade_${tr}`)}
                      {selected && <span className="trade-chip-remove">✕</span>}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="org-form-row">
              <div className="co-form-group">
                <label>{t('account.company_size')}</label>
                <select
                  value={companySize}
                  onChange={(e) => setCompanySize(e.target.value)}
                  disabled={createLoading || loadingOrg}
                >
                  <option value="">{t('account.select_company_size')}</option>
                  {COMPANY_SIZES.slice(1).map((sz) => (
                    <option key={sz} value={sz}>{sz}</option>
                  ))}
                </select>
              </div>
              <div className="co-form-group">
                <label>{t('account.country')}</label>
                <select
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  disabled={createLoading || loadingOrg}
                >
                  <option value="">Select country</option>
                  {COUNTRIES.map((code) => (
                    <option key={code} value={code}>
                      {getCountryName(code)}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="co-form-group">
              <label>{t('account.website')}</label>
              <input
                type="url"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                placeholder="https://example.com"
                disabled={createLoading || loadingOrg}
              />
            </div>

            <p className="helper-text org-settings-note">
              {t('account.org_settings_note')}
            </p>
          </form>
          )}
        </div>

        {/* Footer */}
        <div className="co-modal-footer">
          {createMessage.text && (
            <div className={`create-org-message ${createMessage.type}`}>
              {createMessage.type === 'success' ? '✓' : '⚠'} {createMessage.text}
            </div>
          )}
          <div className="create-org-footer-actions">
            <button
              className="btn btn-secondary"
              type="button"
              onClick={handleClose}
              disabled={createLoading || loadingOrg}
            >
              {t('cancel')}
            </button>
            <button
              className="btn btn-primary"
              type="button"
              onClick={handleSubmit}
              disabled={createLoading || loadingOrg || (!editMode && !orgName.trim())}
            >
              {createLoading ? t('loading') : (editMode ? t('account.save_changes') : t('account.create_org_button'))}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
});

CreateOrgModal.displayName = 'CreateOrgModal';

export default CreateOrgModal;
